import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, Pencil, ChevronLeft, Eye, EyeOff, Type, Image as ImageIcon, Heading, ExternalLink } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { usePages } from "@/contexts/PagesContext";
import { LANGS } from "@/i18n";
import ImageUploadField from "@/components/admin/ImageUploadField";
import RichTextEditor from "@/components/RichTextEditor";

const field = "vl-input rounded-lg px-4 py-2.5 w-full";
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
const emptyTranslations = () => ({ de: { title: "", blocks: [] }, en: { title: "", blocks: [] }, tr: { title: "", blocks: [] } });
const newPage = () => ({ _new: true, translations: emptyTranslations(), hero_image: "", show_in_nav: true, published: true });

export default function PagesPanel() {
  const { t } = useLang();
  const { reload: reloadNav } = usePages();
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editLang, setEditLang] = useState("de");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/pages").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditLang("de"); setEditing(newPage()); };
  const startEdit = (p) => {
    const tr = { ...emptyTranslations(), ...(p.translations || {}) };
    ["de", "en", "tr"].forEach((l) => { tr[l] = { title: "", blocks: [], ...(tr[l] || {}) }; });
    setEditLang("de");
    setEditing({ ...p, translations: tr });
  };

  const bucket = () => (editing.translations[editLang] || { title: "", blocks: [] });
  const setTitle = (v) => setEditing((p) => ({ ...p, translations: { ...p.translations, [editLang]: { ...p.translations[editLang], title: v } } }));
  const setBlocks = (updater) => setEditing((p) => {
    const cur = p.translations[editLang].blocks || [];
    const next = typeof updater === "function" ? updater(cur) : updater;
    return { ...p, translations: { ...p.translations, [editLang]: { ...p.translations[editLang], blocks: next } } };
  });
  const addBlock = (type) => setBlocks((b) => [...b, { id: uid(), type, text: "", html: "", url: "", caption: "" }]);
  const updBlock = (i, patch) => setBlocks((b) => b.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delBlock = (i) => setBlocks((b) => b.filter((_, j) => j !== i));
  const moveBlock = (i, dir) => setBlocks((b) => {
    const j = i + dir;
    if (j < 0 || j >= b.length) return b;
    const n = [...b]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const save = async () => {
    const payload = {
      translations: editing.translations,
      hero_image: editing.hero_image || "",
      show_in_nav: editing.show_in_nav,
      published: editing.published,
    };
    if (!(editing.translations.de.title || editing.translations.en.title || editing.translations.tr.title)) {
      toast.error(t("page_title_ph")); return;
    }
    setSaving(true);
    try {
      if (editing._new) await api.post("/admin/pages", payload);
      else await api.put(`/admin/pages/${editing.id}`, payload);
      toast.success(t("save"));
      setEditing(null);
      await load();
      reloadNav();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(t("page_delete_confirm"))) return;
    try { await api.delete(`/admin/pages/${p.id}`); toast.success(t("delete")); await load(); reloadNav(); }
    catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };

  const reorder = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const n = [...list]; [n[i], n[j]] = [n[j], n[i]]; setList(n);
    try { await api.put("/admin/pages-reorder", { ids: n.map((p) => p.id) }); reloadNav(); }
    catch (e) { toast.error(errMsg(e.response?.data?.detail)); load(); }
  };

  if (list === null) return <div className="py-16 text-center"><Loader2 className="animate-spin text-[var(--vl-gold)] mx-auto" /></div>;

  // ---------------- Edit view ----------------
  if (editing) {
    const b = bucket();
    return (
      <div className="space-y-6 pb-24" data-testid="page-editor">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => setEditing(null)} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5" data-testid="page-back-btn"><ChevronLeft size={14} /> {t("page_back")}</button>
          <div className="flex items-center gap-2">
            {!editing._new && <button onClick={() => remove(editing)} className="text-red-300 hover:text-red-200 px-3 py-2" data-testid="page-delete-btn"><Trash2 size={16} /></button>}
            <button onClick={save} disabled={saving} className="vl-btn-gold rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="page-save-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("save")}
            </button>
          </div>
        </div>

        {/* shared settings */}
        <div className="vl-card rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("page_hero")}</label>
            <ImageUploadField value={editing.hero_image} onChange={(url) => setEditing((p) => ({ ...p, hero_image: url }))} testid="page-hero-upload" />
          </div>
          <div className="flex flex-wrap gap-6">
            <button onClick={() => setEditing((p) => ({ ...p, show_in_nav: !p.show_in_nav }))} className="flex items-center gap-2 text-sm text-[var(--vl-ivory)]" data-testid="page-nav-toggle">
              {editing.show_in_nav ? <Eye size={16} className="text-[var(--vl-gold)]" /> : <EyeOff size={16} className="text-[var(--vl-stone)]" />} {t("page_show_nav")}
            </button>
            <button onClick={() => setEditing((p) => ({ ...p, published: !p.published }))} className="flex items-center gap-2 text-sm text-[var(--vl-ivory)]" data-testid="page-published-toggle">
              <span className={`w-2.5 h-2.5 rounded-full ${editing.published ? "bg-green-400" : "bg-[var(--vl-stone)]"}`} /> {t("page_published")}
            </button>
          </div>
        </div>

        {/* language tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs tracking-widest uppercase text-[var(--vl-stone)] mr-1">{t("edit_language")}:</span>
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => setEditLang(l.code)}
              className={`px-4 py-1.5 rounded-full text-xs tracking-widest uppercase border transition-colors ${editLang === l.code ? "border-[var(--vl-gold)] text-[var(--vl-gold)] bg-[rgba(212,175,55,0.08)]" : "border-[var(--vl-border)] text-[var(--vl-stone)]"}`}
              data-testid={`page-lang-${l.code}`}>{l.label}</button>
          ))}
        </div>

        <div className="vl-card rounded-xl p-6 space-y-4">
          <input className={field} value={b.title} onChange={(e) => setTitle(e.target.value)} placeholder={t("page_title_ph")} data-testid="page-title-input" />

          {(b.blocks || []).map((blk, i) => (
            <div key={blk.id} className="border border-[var(--vl-border)] rounded-xl p-4 space-y-3" data-testid={`page-block-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs tracking-widest uppercase text-[var(--vl-gold)] flex items-center gap-1.5">
                  {blk.type === "heading" ? <Heading size={13} /> : blk.type === "image" ? <ImageIcon size={13} /> : <Type size={13} />}
                  {blk.type === "heading" ? t("page_add_heading") : blk.type === "image" ? t("page_add_image") : t("page_add_text")}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveBlock(i, -1)} className="p-1.5 text-[var(--vl-stone)] hover:text-[var(--vl-gold)]"><ArrowUp size={14} /></button>
                  <button onClick={() => moveBlock(i, 1)} className="p-1.5 text-[var(--vl-stone)] hover:text-[var(--vl-gold)]"><ArrowDown size={14} /></button>
                  <button onClick={() => delBlock(i)} className="p-1.5 text-red-300 hover:text-red-200" data-testid={`page-block-del-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
              {blk.type === "heading" && (
                <input className={field} value={blk.text} onChange={(e) => updBlock(i, { text: e.target.value })} placeholder={t("page_add_heading")} data-testid={`page-block-heading-${i}`} />
              )}
              {blk.type === "text" && (
                <RichTextEditor key={`${editLang}-${blk.id}`} value={blk.html} onChange={(html) => updBlock(i, { html })} testid={`page-block-text-${i}`} />
              )}
              {blk.type === "image" && (
                <div className="space-y-2">
                  <ImageUploadField value={blk.url} onChange={(url) => updBlock(i, { url })} testid={`page-block-image-${i}`} />
                  <input className={field} value={blk.caption} onChange={(e) => updBlock(i, { caption: e.target.value })} placeholder={t("page_block_caption")} />
                </div>
              )}
            </div>
          ))}

          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={() => addBlock("heading")} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5" data-testid="add-block-heading"><Plus size={13} /> {t("page_add_heading")}</button>
            <button onClick={() => addBlock("text")} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5" data-testid="add-block-text"><Plus size={13} /> {t("page_add_text")}</button>
            <button onClick={() => addBlock("image")} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5" data-testid="add-block-image"><Plus size={13} /> {t("page_add_image")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- List view ----------------
  return (
    <div className="space-y-5" data-testid="pages-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-xl text-[var(--vl-ivory)]">{t("pages_tab")}</h3>
        <button onClick={startNew} className="vl-btn-gold rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="page-new-btn"><Plus size={15} /> {t("page_new")}</button>
      </div>

      {list.length === 0 ? (
        <p className="text-center text-[var(--vl-stone)] py-16">{t("page_no_pages")}</p>
      ) : (
        <div className="space-y-3">
          {list.map((p, i) => (
            <div key={p.id} className="vl-card rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid={`page-row-${p.slug}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => reorder(i, -1)} className="text-[var(--vl-stone)] hover:text-[var(--vl-gold)]"><ArrowUp size={14} /></button>
                <button onClick={() => reorder(i, 1)} className="text-[var(--vl-stone)] hover:text-[var(--vl-gold)]"><ArrowDown size={14} /></button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[var(--vl-ivory)] font-medium truncate">{(p.translations?.de?.title) || (p.translations?.en?.title) || (p.translations?.tr?.title) || p.slug}</div>
                <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--vl-gold)] inline-flex items-center gap-1">/{p.slug} <ExternalLink size={11} /></a>
              </div>
              <div className="flex items-center gap-3">
                {p.show_in_nav && <span className="text-[10px] tracking-widest uppercase text-[var(--vl-gold)] border border-[var(--vl-border)] rounded-full px-2.5 py-1">{t("page_show_nav")}</span>}
                <span className={`text-[10px] tracking-widest uppercase rounded-full px-2.5 py-1 border ${p.published ? "text-green-300 border-green-500/30" : "text-[var(--vl-stone)] border-[var(--vl-border)]"}`}>{p.published ? t("page_published") : "—"}</span>
                <button onClick={() => startEdit(p)} className="text-[var(--vl-gold)] hover:text-[var(--vl-gold-bright)] p-2" data-testid={`page-edit-${p.slug}`}><Pencil size={16} /></button>
                <button onClick={() => remove(p)} className="text-red-300 hover:text-red-200 p-2" data-testid={`page-del-${p.slug}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
